/**
 * SoftSkillsDebriefPage — v10.7.4
 * Debriefing emocional post-simulación: timeline visual del estado emocional +
 * secuencia de choices con impacto y feedback.
 *
 * Consume:
 *   GET /soft-skills/sessions/{id}/timeline → { events: [...] }
 *   POST /soft-skills/sessions/{id}/score   → score final si aún no calculado
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, CheckCircle, WarningCircle, ChatCircle, Sparkle, Trophy } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toast } from 'sonner';

const METRIC_META = {
  trust:              { label: 'Confianza',       color: '#10B981' },
  anxiety:            { label: 'Ansiedad',        color: '#F59E0B' },
  anger:              { label: 'Enojo',           color: '#EF4444' },
  understanding:      { label: 'Comprensión',     color: '#06B6D4' },
  cooperation:        { label: 'Cooperación',     color: '#8B5CF6' },
  rapport:            { label: 'Rapport',         color: '#EC4899' },
  emotional_distress: { label: 'Distrés',         color: '#F97316' },
  satisfaction:       { label: 'Satisfacción',    color: '#22C55E' },
  sadness:            { label: 'Tristeza',        color: '#6366F1' },
};

const DEFAULT_VISIBLE = ['trust', 'anxiety', 'anger', 'understanding'];

export default function SoftSkillsDebriefPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [score, setScore] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [visible, setVisible] = useState(DEFAULT_VISIBLE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [timelineRes, scoreRes] = await Promise.all([
          api.get(`/soft-skills/sessions/${sessionId}/timeline`),
          api.post(`/soft-skills/sessions/${sessionId}/score`).catch(() => null),
        ]);
        setEvents(timelineRes.data?.events || []);
        if (scoreRes?.data) setScore(scoreRes.data);
        // Load scenario for interlocutor name
        try {
          const s = await api.get(`/simulations/${sessionId}`);
          if (s?.data?.scenario_id) {
            const sc = await api.get(`/scenarios/${s.data.scenario_id}`);
            setScenario(sc.data);
          }
        } catch { /* silent */ }
      } catch (e) {
        toast.error('No se pudo cargar el debriefing');
      } finally {
        setLoading(false);
      }
    })();
  }, [sessionId]);

  // Chart data: cada evento aporta un punto por métrica visible
  const chartData = useMemo(() => {
    return events.map((e, i) => {
      const snap = e.state_snapshot || {};
      const row = { idx: i + 1, stage: e.stage_id || '?', choice: e.choice_label || '' };
      Object.keys(METRIC_META).forEach((k) => {
        if (k in snap) row[k] = Number(snap[k]);
      });
      return row;
    });
  }, [events]);

  const toggleMetric = (k) => {
    setVisible((prev) => (prev.includes(k) ? prev.filter((m) => m !== k) : [...prev, k]));
  };

  const interlocutorName = ((scenario || {}).soft_skills_config || {}).interlocutor?.name || 'Interlocutor';

  if (loading) return <div className="p-8 text-slate-500">Cargando debriefing…</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="soft-skills-debrief-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/scenarios')}><ArrowLeft size={20} /></Button>
          <div>
            <Badge className="bg-purple-100 text-purple-700 border-purple-200 mb-1">Debriefing emocional</Badge>
            <h1 className="text-2xl font-bold text-slate-900">{scenario?.nombre || 'Sesión'}</h1>
            <p className="text-sm text-slate-500">Interlocutor: <span className="font-medium text-slate-700">{interlocutorName}</span> · {events.length} eventos</p>
          </div>
        </div>
        {score && (
          <div className="text-right">
            <div className="flex items-center justify-end gap-2 mb-1">
              <Trophy size={16} className="text-cyan-600" weight="fill" />
              <span className="text-[10px] font-bold uppercase text-slate-500 tracking-widest">Score final</span>
            </div>
            <div className="text-3xl font-bold text-cyan-600">{score.final_score}</div>
            <div className="text-xs text-slate-500">
              Completitud <strong>{score.completion_pct}%</strong> · Calidad <strong>{score.quality_pct}%</strong>
            </div>
            <div className="text-xs mt-1">
              <span className="text-emerald-600 font-semibold">{score.good_practices} buenas</span> · <span className="text-red-600 font-semibold">{score.critical_errors} críticas</span>
            </div>
          </div>
        )}
      </div>

      {/* Timeline chart */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Timeline emocional</CardTitle>
            <div className="flex items-center gap-1 flex-wrap">
              {Object.entries(METRIC_META).map(([k, meta]) => (
                <button
                  key={k}
                  onClick={() => toggleMetric(k)}
                  data-testid={`toggle-${k}`}
                  className={`text-[10px] px-2 py-1 rounded-full border transition-all ${visible.includes(k) ? 'text-white' : 'text-slate-400 bg-slate-50 border-slate-200'}`}
                  style={visible.includes(k) ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
                >
                  {meta.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-12">No hay eventos para graficar.</div>
          ) : (
            <div style={{ width: '100%', height: 340 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="idx"
                    tick={{ fontSize: 11 }}
                    label={{ value: 'Evento', position: 'insideBottom', offset: -8, style: { fontSize: 11, fill: '#64748b' } }}
                  />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }}
                    labelFormatter={(v) => `Evento ${v} · Etapa ${chartData[v - 1]?.stage || ''}`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {visible.map((k) => (
                    <Line
                      key={k}
                      type="monotone"
                      dataKey={k}
                      stroke={METRIC_META[k].color}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name={METRIC_META[k].label}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event log */}
      <Card>
        <CardHeader><CardTitle>Secuencia de decisiones</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2" data-testid="event-log">
            {events.length === 0 && <div className="text-slate-500 text-sm text-center py-6">Sin eventos.</div>}
            {events.map((e, i) => {
              const isAI = e.response_text && (e.response_text.length > 0) && e.actor !== 'scenario_engine' && ((e.response || {}).generated_by_ai || false);
              const isCoord = e.actor === 'scenario_engine' || e.source === 'hybrid_coordinator';
              return (
                <div key={i} className="p-3 rounded-lg border border-slate-100 bg-slate-50/60" data-testid={`event-row-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-xs font-mono shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700">{e.stage_id || '?'}</span>
                        {e.is_good_practice && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1 text-[10px]">
                            <CheckCircle size={11} weight="fill" /> Buena práctica
                          </Badge>
                        )}
                        {e.is_critical_error && (
                          <Badge className="bg-red-100 text-red-700 border-red-200 gap-1 text-[10px]">
                            <WarningCircle size={11} weight="fill" /> Error crítico
                          </Badge>
                        )}
                        {isCoord && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1 text-[10px]">
                            Coordinator hybrid
                          </Badge>
                        )}
                        {e.clinical_event_type && (
                          <Badge className="bg-slate-200 text-slate-700 border-slate-300 gap-1 text-[10px]">
                            {e.clinical_event_type}
                          </Badge>
                        )}
                      </div>
                      {e.choice_label && (
                        <div className="text-sm text-slate-800"><strong>Alumno:</strong> {e.choice_label}</div>
                      )}
                      {e.response_text && (
                        <div className="text-sm text-purple-700 mt-1 flex items-start gap-1.5">
                          <ChatCircle size={14} className="text-purple-500 mt-0.5 shrink-0" weight="fill" />
                          <span><strong>{interlocutorName}:</strong> <span className="italic">"{e.response_text}"</span></span>
                          {isAI && <Badge className="bg-violet-100 text-violet-700 border-violet-200 gap-1 text-[9px] ml-1"><Sparkle size={9} weight="fill" /> IA</Badge>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
